import Vue from "vue";
import http from "./http";
import debug from "./notificationDebug";
import clientLog, { safeKey } from "./clientLog";

const MUTED_KEY = "mutedKeywords";
const KNOWN_IDS_KEY = "notificationKnownIds";
const UNREAD_KEY = "notificationUnreadCount";
const BASELINE_KEY = "notificationBaseline";
// Aynı tarayıcıdaki sekmelerin ortak senkron zamanı. Bir kullanıcı 3 sekme
// açtığında sunucuya 3 ayrı istek gitmesini engeller.
const LAST_SYNC_KEY = "notificationLastSyncAt";

// sync() için taban alt sınır. Asıl tempoyu (60 sn / 20 sn) App.vue belirler;
// buradaki değer hangi çağrı yolundan gelinirse gelinsin aşılamayan bant.
const MIN_SYNC_INTERVAL_MS = 15000;

// Paylaşılan zaman damgası bundan eskiyse (ya da gelecek tarihliyse) yok
// sayılır; bozuk bir kayıt sekmeyi süresiz bloklamasın.
const SHARED_SYNC_MAX_AGE_MS = 600000;

// Bir bildirimin tekrar tekrar toast/rozet üretmesini engellemek için tutulan
// anahtar listesinin üst sınırı. FIFO, eski anahtarlar düşer.
const MAX_KNOWN_IDS = 1000;

function safeParse(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed == null ? fallback : parsed;
  } catch (e) {
    // Bozuk localStorage değeri uygulamayı açılmaz hale getirmesin.
    localStorage.removeItem(key);
    return fallback;
  }
}

function safeWrite(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    // Kota dolu / private mode. Bellek içi state ile devam ederiz.
  }
}

function safeRead(key) {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    // Private mode / erişim engeli: sekme kendi zamanıyla devam eder.
    return null;
  }
}

function text(value) {
  return value == null ? "" : String(value);
}

// Pusher payload'u ile /notifications cevabı aynı şemaya indirgenir.
// (lead_name / leadName karışıklığı buradan sonra kalmıyor.)
function normalize(input) {
  let raw = input;
  // Backend payload'u çift kodlarsa (string içinde JSON) yine de okunsun.
  if (typeof raw === "string") {
    try {
      raw = JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }
  if (!raw || typeof raw !== "object") return null;

  const leadName = raw.lead_name || raw.leadName || null;
  const leadId = raw.lead_id || raw.leadId || null;

  const item = {
    id: raw.id != null ? String(raw.id) : null,
    title: text(raw.title),
    body: text(raw.body),
    start: raw.start || raw.created_at || null,
    lead_id: leadId,
    lead_name: leadName,
    leadName: leadName
  };

  // Aynı bildirim iki farklı kaynaktan gelir: Pusher payload'unda id
  // olmayabilir, /notifications cevabında her zaman vardır. Bu yüzden her
  // kayıt için hem id hem de içerik anahtarı üretilir; ikisinden biri
  // eşleşiyorsa bildirim "daha önce görülmüş" sayılır.
  item.keys = [contentKey(item)];
  if (item.id) item.keys.unshift("id:" + item.id);
  item.key = item.keys[0];

  return item;
}

// Tarih formatı kaynağa göre değişebildiği için (ISO / "YYYY-MM-DD HH:mm:ss")
// sadece dakikaya kadar olan rakamlar kullanılır.
function startKey(value) {
  return text(value)
    .replace(/\D/g, "")
    .slice(0, 12);
}

function contentKey(item) {
  return ["h", item.title, item.body, startKey(item.start), item.lead_id].join(
    "|"
  );
}

const state = Vue.observable({
  items: [],
  unreadCount: parseInt(localStorage.getItem(UNREAD_KEY), 10) || 0,
  mutedKeywords: safeParse(MUTED_KEY, []),
  connectionState: "initialized",
  // Masaüstü bildirim izni: default | granted | denied | unsupported
  permission: null,
  lastSyncAt: null,
  lastSyncError: null,
  syncing: false
});

// Socket'ten gelip sunucu listesinde henüz görünmeyen kayıtlar. /notifications
// bu bildirimi döndürmese bile arayüzden kaybolmasın diye ayrı tutulur.
const MAX_LIVE_ITEMS = 50;
let liveItems = [];

// Toast tekrarını engelleyen kayıt. DİKKAT: sync()'in doldurduğu knownIds'ten
// ayrıdır. /notifications bekleyen hatırlatmaları da döndürür; bir kaydın o
// listede olması "kullanıcıya duyuruldu" demek DEĞİLDİR. Sadece socket'ten
// gelen mesajlar buraya yazılır.
//
// Süre sınırlı tutulur: gerçek duplikeler (yeniden bağlanma tekrarı, çift
// publish) saniyeler içinde gelir; dakikalar sonra tekrar yayınlanan bir
// hatırlatma ise kullanıcıya yeniden gösterilmelidir.
const ANNOUNCE_DEDUP_MS = 60000;
const announced = new Map();

// Canlı mesaj bağlantının koptuğu birkaç saniyeye denk gelirse kaybolur;
// kayıt sonraki senkronda listeye ve rozete girer ama kullanıcıya hiç
// duyurulmazdı ("bildirim gelmedi"). Senkronda bulunan yeni kayıtlar artık
// duyurulur; ancak bu tarayıcıdaki önceki senkron bu süreden yeniyse. Daha
// eskiyse (tarayıcı kapalıydı / uykudaydı) sadece rozet artar. Süre sadece
// tarayıcı saatiyle ölçülür; kiracı saat dilimi farkından etkilenmez.
const MISSED_ANNOUNCE_WINDOW_MS = 10 * 60000;
const missedListeners = new Set();

function notifyMissed(items) {
  missedListeners.forEach(cb => {
    try {
      cb(items);
    } catch (e) {
      /* dinleyici hatası senkronu bozmasın */
    }
  });
}

function wasAnnouncedRecently(item) {
  const now = Date.now();
  announced.forEach((ts, key) => {
    if (now - ts > ANNOUNCE_DEDUP_MS) announced.delete(key);
  });
  return item.keys.some(key => announced.has(key));
}

function markAnnounced(item) {
  const now = Date.now();
  item.keys.forEach(key => announced.set(key, now));
}

let knownIds = safeParse(KNOWN_IDS_KEY, []);
if (!Array.isArray(knownIds)) knownIds = [];
let knownIdSet = new Set(knownIds);
let baselineSet = localStorage.getItem(BASELINE_KEY) === "1";

// Sunucu sürekli hata döndürürse (ör. oturum düşmüş) gün boyu 20 sn'de bir
// istek atmamak için artan bekleme.
let failureCount = 0;
let nextAttemptAt = 0;

// Bu sekmenin ve diğer sekmelerin en son başarılı senkronu.
function lastSyncAcrossTabs() {
  const now = Date.now();
  const local = state.lastSyncAt || 0;
  const shared = parseInt(safeRead(LAST_SYNC_KEY), 10) || 0;
  const usable =
    shared > now || now - shared > SHARED_SYNC_MAX_AGE_MS ? 0 : shared;
  return Math.max(local, usable);
}

function hasSeen(item) {
  return item.keys.some(key => knownIdSet.has(key));
}

// Daha önce görülmemişse tüm anahtarlarını kaydeder ve true döner.
function remember(item) {
  if (hasSeen(item)) return false;
  item.keys.forEach(key => {
    if (knownIdSet.has(key)) return;
    knownIdSet.add(key);
    knownIds.push(key);
  });
  if (knownIds.length > MAX_KNOWN_IDS) {
    const dropped = knownIds.splice(0, knownIds.length - MAX_KNOWN_IDS);
    dropped.forEach(k => knownIdSet.delete(k));
  }
  return true;
}

// Sunucu aynı kaydı iki kez döndürürse listede de v-for key'inde de
// çift görünmesin.
function dropDuplicates(items) {
  const seen = Object.create(null);
  const unique = [];
  items.forEach(item => {
    if (item.keys.some(key => seen[key])) return;
    item.keys.forEach(key => {
      seen[key] = true;
    });
    unique.push(item);
  });
  return unique;
}

function persistKnownIds() {
  safeWrite(KNOWN_IDS_KEY, JSON.stringify(knownIds));
}

function setUnread(value) {
  const next = Math.max(0, value);
  state.unreadCount = next;
  safeWrite(UNREAD_KEY, String(next));
}

function isMuted(item) {
  if (!item) return false;
  return state.mutedKeywords.some(
    keyword =>
      keyword &&
      (text(item.body).includes(keyword) ||
        text(item.title).includes(keyword) ||
        text(item.lead_name).includes(keyword))
  );
}

export default {
  state,

  isMuted,

  // Sessize alınmamış bildirimler (liste render'ı bunu kullanır).
  visibleItems() {
    return state.items.filter(item => !isMuted(item));
  },

  /**
   * Sunucudaki listeyi tek doğruluk kaynağı olarak yeniden yükler.
   * Bağlantı koptuğu sırada kaçırılan bildirimler burada yakalanır ve
   * okunmamış sayacına eklenir.
   *
   * options:
   *   force  -> hiçbir kısıt uygulanmaz. Kullanıcının "Yenile" tuşu ve ilk
   *             yükleme için; liste boşken beklemeye girilmemeli.
   *   urgent -> hata backoff'unu atlar ama alt sınıra uyar (yeniden bağlanma).
   *   minAge -> son senkronun (bu sekme veya kardeş sekmeler) üzerinden bu
   *             kadar ms geçmediyse istek atılmaz.
   */
  sync(options) {
    // Geriye dönük uyumluluk: sync(true) === sync({ force: true })
    const opts = options === true ? { force: true } : options || {};
    const force = opts.force === true;
    const urgent = opts.urgent === true;

    if (state.syncing) return Promise.resolve(state.items);

    if (!force) {
      const minAge =
        typeof opts.minAge === "number" ? opts.minAge : MIN_SYNC_INTERVAL_MS;
      // Son senkronun (bu sekme veya kardeş sekmeler) üzerinden yeterli süre
      // geçmediyse istek atılmaz.
      if (Date.now() - lastSyncAcrossTabs() < minAge)
        return Promise.resolve(state.items);
      // Sunucu hata veriyorsa artan bekleme. Yeniden bağlanma (urgent) bunu
      // atlar: kopukluk zaten hataların sebebi olmuş olabilir.
      if (!urgent && Date.now() < nextAttemptAt)
        return Promise.resolve(state.items);
    }

    state.syncing = true;
    // Bu senkrondan önceki son başarılı senkron (bu sekme, kardeş sekmeler ya
    // da önceki sayfa yüklemesi).
    const previousSyncAt = lastSyncAcrossTabs();

    return http
      .getNotifications()
      .then(resp => {
        const results = (resp && resp.data && resp.data.results) || [];
        const normalized = dropDuplicates(
          results.map(normalize).filter(Boolean)
        );

        const missedItems = [];
        normalized.forEach(item => {
          if (remember(item) && baselineSet && !isMuted(item)) {
            missedItems.push(item);
          }
        });
        const missed = missedItems.length;

        if (!baselineSet) {
          // İlk yükleme: mevcut kayıtlar "kaçırılmış" sayılmaz.
          baselineSet = true;
          safeWrite(BASELINE_KEY, "1");
        }

        persistKnownIds();

        // Liste sunucu cevabıyla DEĞİŞTİRİLMEZ, BİRLEŞTİRİLİR: sunucu kaydı
        // esas alınır, sunucuda olmayan canlı kayıtlar korunur.
        const serverKeys = Object.create(null);
        normalized.forEach(item => {
          item.keys.forEach(key => {
            serverKeys[key] = true;
          });
        });
        liveItems = liveItems.filter(
          item => !item.keys.some(key => serverKeys[key])
        );
        state.items = dropDuplicates(liveItems.concat(normalized));

        if (missed > 0) setUnread(state.unreadCount + missed);
        state.lastSyncAt = Date.now();
        // Kardeş sekmeler bu zamanı görüp gereksiz istek atmasın.
        safeWrite(LAST_SYNC_KEY, String(state.lastSyncAt));
        state.lastSyncError = null;
        state.syncing = false;
        failureCount = 0;
        nextAttemptAt = 0;

        if (
          missedItems.length &&
          previousSyncAt &&
          Date.now() - previousSyncAt <= MISSED_ANNOUNCE_WINDOW_MS
        ) {
          // Canlı mesaj geç gelirse ikinci kez duyurulmasın (ingest bunu görür).
          const fresh = missedItems.filter(item => !wasAnnouncedRecently(item));
          fresh.forEach(markAnnounced);
          if (fresh.length) {
            debug("store: senkronda kaçırılmış bildirim", {
              open: fresh.length
            });
            notifyMissed(fresh);
          }
        }
        return normalized;
      })
      .catch(err => {
        // Hata durumunda eldeki liste korunur, bir sonraki tur tekrar dener.
        state.lastSyncError = err;
        state.syncing = false;
        failureCount++;
        const status = err && err.response ? err.response.status : null;
        // 401: oturum süresi dolmuş; beklenen durum, router girişe yönlendirir.
        if (status !== 401) {
          clientLog.warn("notify.sync_failed", {
            message: String((err && err.message) || err).slice(0, 200),
            status,
            failures: failureCount
          });
        }
        nextAttemptAt = Date.now() + Math.min(30000 * failureCount, 300000);
        return state.items;
      });
  },

  /**
   * Canlı (Pusher) mesajı işler.
   * Daha önce görülmüş bir bildirimse null döner -> toast/masaüstü bildirimi
   * tekrarlanmaz (yeniden bağlanma tekrarları, çoklu teslimat).
   * Sessize alınmışsa listeye girer ama sayaç artmaz ve null döner.
   */
  ingest(raw) {
    const item = normalize(raw);
    if (!item) {
      debug("store: payload çözümlenemedi", raw);
      clientLog.warn("notify.parse_failed", { type: typeof raw });
      return null;
    }

    // Sadece aynı mesajın kısa süre içinde tekrar teslimi bastırılır.
    if (wasAnnouncedRecently(item)) {
      debug("store: son 60 sn içinde zaten duyuruldu, bastırıldı", item.key);
      return null;
    }
    markAnnounced(item);

    // Rozet için "biliniyor" olarak işaretle ki sonraki sync bunu ikinci kez
    // "kaçırılmış" saymasın.
    remember(item);
    persistKnownIds();
    liveItems = [item].concat(liveItems).slice(0, MAX_LIVE_ITEMS);
    // Kayıt zaten sunucu listesinden gelmiş olabilir (bekleyen hatırlatma);
    // eskisinin üstüne yaz, listede çift satır oluşmasın.
    state.items = dropDuplicates([item].concat(state.items));

    if (isMuted(item)) {
      debug("store: sessize alınmış kelime eşleşti, toast yok", {
        key: item.key,
        mutedKeywords: state.mutedKeywords.slice()
      });
      // "Bildirim gelmiyor" şikâyetinin sık sebebi: unutulmuş bir kelime.
      clientLog.info(
        "notify.muted",
        {
          id: item.id,
          key: safeKey(item.key),
          mutedCount: state.mutedKeywords.length
        },
        { dedup: false }
      );
      return null;
    }

    setUnread(state.unreadCount + 1);
    debug("store: kabul edildi, toast'a gidiyor", item.key);
    return item;
  },

  // Senkronda bulunan, canlı gelmemiş bildirimler için. Aboneliği kaldıran
  // fonksiyon döner.
  onMissed(cb) {
    missedListeners.add(cb);
    return () => missedListeners.delete(cb);
  },

  markAllSeen() {
    setUnread(0);
  },

  setConnectionState(value) {
    state.connectionState = value;
  },

  setPermission(value) {
    state.permission = value;
  },

  addMutedKeyword(keyword) {
    const value = text(keyword).trim();
    if (!value || state.mutedKeywords.includes(value)) return false;
    state.mutedKeywords = state.mutedKeywords.concat(value);
    safeWrite(MUTED_KEY, JSON.stringify(state.mutedKeywords));
    return true;
  },

  removeMutedKeyword(keyword) {
    state.mutedKeywords = state.mutedKeywords.filter(item => item !== keyword);
    safeWrite(MUTED_KEY, JSON.stringify(state.mutedKeywords));
  },

  reloadMutedKeywords() {
    state.mutedKeywords = safeParse(MUTED_KEY, []);
  }
};
